import type { Session } from '@supabase/supabase-js';
import { authConfig } from './config';

const FIREBASE_AUTH_ENDPOINT = 'https://identitytoolkit.googleapis.com/v1/accounts:signInWithCustomToken';
const BRIDGE_FUNCTION_NAME = 'firebaseCustomToken';
const REQUEST_TIMEOUT_MS = 12_000;
const FIREBASE_TOKEN_SAFETY_WINDOW_MS = 60_000;

type JsonRecord = Record<string, unknown>;

type FirebaseIdentityCache = {
  userId: string;
  idToken: string;
  expiresAt: number;
};

let bridgeCache: FirebaseIdentityCache | null = null;
let bridgeGeneration = 0;
let pendingBridge: { userId: string; promise: Promise<FirebaseIdentityCache> } | null = null;

const normalizeBaseUrl = (value: string): string => value.trim().replace(/\/+$/, '');

const isRecord = (value: unknown): value is JsonRecord => (
  typeof value === 'object' && value !== null && !Array.isArray(value)
);

const getIdentifierSuffix = (value: string | null | undefined): string | null => {
  const normalized = value?.trim() ?? '';
  return normalized ? normalized.slice(-6) : null;
};

const getJwtSubject = (token: string): string | null => {
  try {
    const [, payload] = token.split('.');
    if (!payload) return null;
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(payload.length / 4) * 4, '=');
    const decoded = JSON.parse(atob(base64)) as { sub?: unknown };
    return typeof decoded.sub === 'string' && decoded.sub.trim() ? decoded.sub.trim() : null;
  } catch {
    return null;
  }
};

const requestJson = async (
  url: string,
  init: RequestInit,
  label: string,
): Promise<{ status: number; body: JsonRecord }> => {
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

  try {
    const response = await fetch(url, { ...init, signal: controller.signal });
    const rawBody: unknown = await response.json().catch(() => ({}));
    return {
      status: response.status,
      body: isRecord(rawBody) ? rawBody : {},
    };
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw new Error(`${label}逾時，請稍後再試。`);
    }
    throw new Error(`${label}無法連線，請稍後再試。`);
  } finally {
    window.clearTimeout(timeoutId);
  }
};

const getErrorMessage = (body: JsonRecord, fallback: string): string => {
  const nestedError = isRecord(body.error) && typeof body.error.message === 'string'
    ? body.error.message
    : null;
  const directError = typeof body.error === 'string' ? body.error : null;
  return nestedError || directError || fallback;
};

const requireFirebaseConfig = (): void => {
  if (!authConfig.flight404FirebaseApiKey.trim()) {
    throw new Error('Flight404 Firebase bridge 尚未設定，登入仍可使用，但 Firebase 身份尚未建立。');
  }
  if (!authConfig.flight404FirebaseProjectId.trim()) {
    throw new Error('Flight404 Firebase project 尚未設定，登入仍可使用，但 Firebase 身份尚未建立。');
  }
};

const getFirebaseIdToken = async (session: Session): Promise<FirebaseIdentityCache> => {
  requireFirebaseConfig();

  const userId = session.user.id;
  if (bridgeCache && bridgeCache.userId !== userId) {
    bridgeGeneration += 1;
    bridgeCache = null;
  }
  if (
    bridgeCache?.userId === userId
    && bridgeCache.expiresAt > Date.now() + FIREBASE_TOKEN_SAFETY_WINDOW_MS
  ) {
    return bridgeCache;
  }

  if (pendingBridge?.userId === userId) return pendingBridge.promise;

  const generationAtStart = bridgeGeneration;
  const promise = (async () => {
    const functionsBaseUrl = normalizeBaseUrl(authConfig.flight404FirebaseFunctionsBaseUrl);
    const bridgeResponse = await requestJson(
      `${functionsBaseUrl}/${BRIDGE_FUNCTION_NAME}`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
      },
      'Flight404 Firebase 身份橋接',
    );

    if (bridgeResponse.status < 200 || bridgeResponse.status >= 300) {
      throw new Error(getErrorMessage(bridgeResponse.body, 'Flight404 Firebase 身份橋接失敗。'));
    }

    const customToken = typeof bridgeResponse.body.customToken === 'string'
      ? bridgeResponse.body.customToken.trim()
      : '';
    if (!customToken) {
      throw new Error('Flight404 Firebase 身份橋接沒有回傳認證資料。');
    }

    const authResponse = await requestJson(
      `${FIREBASE_AUTH_ENDPOINT}?key=${encodeURIComponent(authConfig.flight404FirebaseApiKey)}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: customToken, returnSecureToken: true }),
      },
      'Firebase Auth 登入',
    );

    if (authResponse.status < 200 || authResponse.status >= 300) {
      throw new Error(getErrorMessage(authResponse.body, 'Firebase Auth 登入失敗。'));
    }

    const idToken = typeof authResponse.body.idToken === 'string'
      ? authResponse.body.idToken.trim()
      : '';
    const localId = typeof authResponse.body.localId === 'string'
      ? authResponse.body.localId.trim()
      : '';
    const expiresInSeconds = Number(authResponse.body.expiresIn);
    const firebaseUidFromIdToken = getJwtSubject(idToken);
    const firebaseUid = localId || firebaseUidFromIdToken || '';

    if (!idToken || !firebaseUid || firebaseUid !== userId || !Number.isFinite(expiresInSeconds)) {
      const jwtSubject = getJwtSubject(session.access_token);
      console.warn('[Bubble Game] Flight404 bridge identity mismatch', {
        sessionUidSuffix: getIdentifierSuffix(userId),
        jwtSubjectSuffix: getIdentifierSuffix(jwtSubject),
        firebaseLocalIdSuffix: getIdentifierSuffix(localId),
        firebaseIdTokenSubjectSuffix: getIdentifierSuffix(firebaseUidFromIdToken),
        firebaseUidSource: localId ? 'localId' : firebaseUidFromIdToken ? 'idToken.sub' : null,
        firebaseUidSuffix: getIdentifierSuffix(firebaseUid),
        sessionMatchesJwtSubject: jwtSubject ? jwtSubject === userId : null,
        sessionMatchesFirebase: Boolean(firebaseUid) && firebaseUid === userId,
        jwtSubjectMatchesFirebase: jwtSubject && firebaseUid ? jwtSubject === firebaseUid : null,
        hasFirebaseIdToken: Boolean(idToken),
        hasFiniteExpiry: Number.isFinite(expiresInSeconds),
      });
      throw new Error('Firebase Auth 身份與 GGMOMOMO 帳戶不一致。');
    }

    if (generationAtStart !== bridgeGeneration) {
      throw new Error('Flight404 Firebase 身份橋接已過期，請重新登入。');
    }

    const nextCache = {
      userId,
      idToken,
      expiresAt: Date.now() + Math.max(0, expiresInSeconds - 60) * 1000,
    };
    bridgeCache = nextCache;
    return nextCache;
  })();
  const pendingRequest = { userId, promise };
  pendingBridge = pendingRequest;

  try {
    return await promise;
  } finally {
    if (pendingBridge === pendingRequest) pendingBridge = null;
  }
};

export async function bridgeSupabaseSessionToFlight404(session: Session | null): Promise<void> {
  if (!session) {
    bridgeGeneration += 1;
    bridgeCache = null;
    pendingBridge = null;
    return;
  }
  await getFirebaseIdToken(session);
}
